import type { StatsGranularity } from '@/shared/lib'

// Re-exported so this feature's own files keep importing their types from one
// place; the window itself is shared, because the leads table asks for the same
// one the counters above it do.
export type { StatsGranularity }

/** The 15 traffic sources, keyed exactly as the wire has them — the PATCH is a
 * full replace, so keeping the wire keys avoids a 30-line mapping both ways. */
export const SOURCE_KEYS = [
  'google_search',
  'google_ads',
  'meta_ads',
  'facebook',
  'instagram',
  'x',
  'x_ads',
  'reddit',
  'reddit_ads',
  'bing_search',
  'bing_ads',
  'yandex_search',
  'yandex_ads',
  'telegram',
  'whatsapp',
] as const

export type SourceKey = (typeof SOURCE_KEYS)[number]

export type CampaignSources = Record<SourceKey, boolean>

export type Campaign = {
  id: string
  name: string
  isActive: boolean
  whiteOnly: boolean
  createdAt: string
}

export type CampaignDetail = Campaign & { updatedAt: string }

export type PageType = 'redirect' | 'include'

export type CampaignFilters = {
  whiteType: PageType
  whitePage: string
  blackType: PageType
  blackPage: string
  blacklistIp: string[]
  blacklistIsp: string[]
  blacklistDns: string[]
  allowedCountries: string[]
  allowedLanguages: string[]
  allowedDevices: string[]
  allowedOs: string[]
  customUtm: string[]
  customSources: string[]
  allowVpn: boolean
}

export type StatsRange = { from: string; to: string; granularity: StatsGranularity }

/**
 * One cell of the type × page cross-tab.
 *
 * `type` and `page` are independent and both exhaustive — every hit carries a
 * verdict (bot / abuse / lead) *and* the page it was served — so these are the
 * margins of a table, never steps of a funnel.
 */
export type StatsCell = { type: string; page: string; hits: number }

/** One row of a breakdown. `leads` rides along so every slice has a rate. */
export type StatsSlice = { key: string; hits: number; leads: number }

/**
 * The endpoint also returns a time series and the block-reason breakdown.
 * Nothing draws either, so they are not modelled here — the response schema
 * drops them on the way in.
 */
export type CampaignReport = {
  range: StatsRange
  matrix: StatsCell[]
  devices: StatsSlice[]
  countries: StatsSlice[]
  sources: StatsSlice[]
}
